<template>
  <div v-if="show" class="modal-backdrop" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-content export-columns-modal">
        <div class="modal-header">
          <h2>{{ $t('exportColumns.title') }}</h2>
          <button @click="closeModal" class="btn btn-icon">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>

        <div class="modal-body">
        <div class="columns-instructions">
          <p>{{ $t('exportColumns.instructions') }}</p>
        </div>

        <div class="columns-search">
          <input 
            v-model="searchQuery"
            type="text" 
            :placeholder="$t('exportColumns.search')"
            class="search-input"
          />
        </div>

        <div class="columns-actions">
          <button @click="selectAll" class="btn-secondary btn-sm">
            <LineIcon name="check-double" :size="15" :stroke-width="1.8" />
            {{ $t('exportColumns.selectAll') }}
          </button>
          <button @click="deselectAll" class="btn-secondary btn-sm">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
            {{ $t('exportColumns.deselectAll') }}
          </button>
          <button @click="resetToDefaults" class="btn-secondary btn-sm">
            <LineIcon name="rotate-left" :size="15" :stroke-width="1.8" />
            {{ $t('exportColumns.reset') }}
          </button>
        </div>

        <div class="type-filter-bar">
          <span class="type-filter-label">{{ $t('exportColumns.settingsFor') }}:</span>
          <div class="type-filter-buttons">
            <button
              @click="switchTypeFilter('purchase')"
              class="type-filter-btn"
              :class="{ active: activeTypeFilter === 'purchase' }"
            >
              {{ $t('exportColumns.typePurchase') }}
            </button>
            <button 
              @click="switchTypeFilter('sale')" 
              class="type-filter-btn"
              :class="{ active: activeTypeFilter === 'sale' }"
            >
              {{ $t('exportColumns.typeSale') }}
            </button>
          </div>
        </div>

        <div class="columns-list">
          <draggable 
            v-model="localColumns" 
            item-key="key"
            handle=".drag-handle"
            @end="onDragEnd"
          >
            <template #item="{element, index}">
              <div 
                v-if="matchesSearch(element)"
                class="column-item"
                :class="{ disabled: !element.enabled }"
              >
                <div class="drag-handle">
                  <LineIcon name="grip" :size="15" :stroke-width="1.8" />
                </div>
                
                <input 
                  type="number" 
                  class="column-order" 
                  :value="index + 1" 
                  min="1" 
                  :max="localColumns.length"
                  @change="onOrderChange(index, $event)"
                  @focus="$event.target.select()"
                />
                
                <label class="column-toggle">
                  <input 
                    type="checkbox" 
                    v-model="element.enabled"
                    @change="onColumnToggle"
                  />
                  <span class="toggle-track"></span>
                </label>
                
                <div class="column-info">
                  <div class="column-label">{{ translateColumnLabel(element.key, element.label) }}</div>
                  <div class="column-key">{{ element.key }}</div>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-left">
          <div class="selected-count">
            {{ selectedCount }} / {{ totalCount }} {{ $t('exportColumns.columnsSelected') }}
          </div>
        </div>
        <div class="footer-right">
          <button @click="closeModal" class="btn-secondary">
            {{ $t('common.cancel') }}
          </button>
          <button @click="saveSettings" class="btn-primary" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="fa-solid fa-spinner" spin />
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import axios from 'axios';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  companyId: {
    type: Number,
    default: null
  },
  exportFormat: {
    type: String,
    default: 'excel'
  }
});

const emit = defineEmits(['close', 'saved']);
const { t, te } = useI18n();

const translateColumnLabel = (key, fallback) => {
  const i18nKey = `exportColumns.columns.${key}`;
  return te(i18nKey) ? t(i18nKey) : fallback;
};

const localColumns = ref([]);
const searchQuery = ref('');
const saving = ref(false);
const activeTypeFilter = ref('purchase');

// Computed
const selectedCount = computed(() => localColumns.value.filter(col => col.enabled).length);
const totalCount = computed(() => localColumns.value.length);

// Methods
const matchesSearch = (column) => {
  if (!searchQuery.value) return true;
  
  const query = searchQuery.value.toLowerCase();
  const translatedLabel = translateColumnLabel(column.key, column.label);
  return translatedLabel.toLowerCase().includes(query) || 
         column.label.toLowerCase().includes(query) || 
         column.key.toLowerCase().includes(query);
};

const loadColumns = async () => {
  try {
    const response = await axios.get('/api/export-column-settings', {
      params: {
        company_id: props.companyId,
        export_format: props.exportFormat,
        type_filter: activeTypeFilter.value
      }
    });
    
    localColumns.value = response.data.columns.map((col, index) => ({
      ...col,
      order: col.order ?? index + 1
    }));
  } catch (error) {
    console.error('Error loading columns:', error);
    // Load defaults
    await loadDefaultColumns();
  }
};

const loadDefaultColumns = async () => {
  try {
    const response = await axios.get('/api/export-column-settings/available', {
      params: {
        export_format: props.exportFormat
      }
    });
    
    localColumns.value = response.data.columns;
  } catch (error) {
    console.error('Error loading default columns:', error);
  }
};

const saveSettings = async () => {
  saving.value = true;
  
  try {
    // Update order based on current position
    const columnsToSave = localColumns.value.map((col, index) => ({
      key: col.key,
      label: col.label,
      enabled: col.enabled,
      order: index + 1
    }));
    
    await axios.post('/api/export-column-settings', {
      company_id: props.companyId,
      export_format: props.exportFormat,
      type_filter: activeTypeFilter.value,
      columns: columnsToSave
    });
    
    emit('saved', columnsToSave);
    closeModal();
  } catch (error) {
    console.error('Error saving column settings:', error);
    alert(t('exportColumns.saveFailed'));
  } finally {
    saving.value = false;
  }
};

const resetToDefaults = async () => {
  if (!confirm(t('exportColumns.resetConfirm'))) {
    return;
  }
  
  try {
    const response = await axios.post('/api/export-column-settings/reset', {
      company_id: props.companyId,
      export_format: props.exportFormat,
      type_filter: activeTypeFilter.value
    });
    
    localColumns.value = response.data.columns;
  } catch (error) {
    console.error('Error resetting columns:', error);
  }
};

const selectAll = () => {
  localColumns.value.forEach(col => {
    col.enabled = true;
  });
};

const deselectAll = () => {
  localColumns.value.forEach(col => {
    col.enabled = false;
  });
};

const onDragEnd = () => {
  // Reorder is handled automatically by draggable
  // Just need to update order numbers
  localColumns.value.forEach((col, index) => {
    col.order = index + 1;
  });
};

const onOrderChange = (currentIndex, event) => {
  let newIndex = parseInt(event.target.value) - 1;
  if (isNaN(newIndex) || newIndex < 0) newIndex = 0;
  if (newIndex >= localColumns.value.length) newIndex = localColumns.value.length - 1;
  
  const item = localColumns.value.splice(currentIndex, 1)[0];
  localColumns.value.splice(newIndex, 0, item);
  localColumns.value.forEach((col, i) => {
    col.order = i + 1;
  });
};

const onColumnToggle = () => {
  // Could add logic here if needed
};

const switchTypeFilter = async (type) => {
  activeTypeFilter.value = type;
  await loadColumns();
};

const closeModal = () => {
  emit('close');
};

// Watch for show prop changes
watch(() => props.show, (newVal) => {
  if (newVal) {
    activeTypeFilter.value = 'purchase';
    loadColumns();
    searchQuery.value = '';
  }
});
</script>

<style scoped>
/* Use global modal-backdrop and modal-container from app.css */
/* Custom styles only for specific modal content */

.export-columns-modal {
  /* Override global modal-content height to extend to max */
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
}

.modal-header {
  /* Modal header is already styled globally, just add specific overrides */
  flex-shrink: 0;
  border-radius: 1rem 1rem 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  /* Override global modal-body to work with flex layout */
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.columns-instructions {
  padding: 12px;
  background: var(--info-bg, #eff6ff);
  border-radius: 8px;
  border-left: 3px solid var(--info-border, #3b82f6);
}

.columns-instructions p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary, #374151);
}

.columns-search {
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 8px;
  font-size: 14px;
}

.columns-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--bg-secondary, #f9fafb);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
}

.type-filter-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
}

.type-filter-buttons {
  display: flex;
  gap: 4px;
}

.type-filter-btn {
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 6px;
  background: #fff;
  color: var(--text-primary, #374151);
  cursor: pointer;
  transition: all 0.15s;
}

.type-filter-btn:hover {
  background: #f3f4f6;
}

.type-filter-btn.active {
  background: var(--primary, #4f8cff);
  color: #fff;
  border-color: var(--primary, #4f8cff);
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.columns-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  padding: 8px;
  background: var(--bg-secondary, #f9fafb);
  min-height: 0;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.column-item:hover {
  border-color: var(--primary, #4f8cff);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.column-item.disabled {
  opacity: 0.5;
}

.drag-handle {
  cursor: grab;
  color: var(--text-tertiary, #9ca3af);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.drag-handle:active {
  cursor: grabbing;
}

.column-order {
  width: 40px;
  height: 32px;
  text-align: center;
  background: var(--bg-tertiary, #f3f4f6);
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  padding: 0 4px;
  transition: border-color 0.15s ease;
  -moz-appearance: textfield;
}

.column-order::-webkit-outer-spin-button,
.column-order::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.column-order:focus {
  outline: none;
  border-color: var(--primary-color, #3b82f6);
  background: #fff;
}

.column-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
}

.column-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ccc;
  border-radius: 10px;
  transition: background 0.2s;
}

.toggle-track::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.column-toggle input:checked + .toggle-track {
  background: var(--primary, #4f8cff);
}

.column-toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.column-info {
  flex: 1;
  min-width: 0;
}

.column-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #111827);
  margin-bottom: 2px;
}

.column-key {
  font-size: 12px;
  color: var(--text-tertiary, #9ca3af);
  font-family: monospace;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
  background: var(--bg-secondary, #f9fafb);
  flex-shrink: 0;
  border-radius: 0 0 1rem 1rem;
}

.footer-left {
  flex: 1;
}

.selected-count {
  font-size: 13px;
  color: var(--text-secondary, #6b7280);
  font-weight: 500;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: white;
  border: 1px solid var(--border-color, #d1d5db);
  color: var(--text-primary, #374151);
}

.btn-secondary:hover {
  background: var(--hover-bg, #f3f4f6);
}

.btn-primary {
  background: var(--primary, #4f8cff);
  color: white;
}

.btn-primary:hover {
  background: var(--primary-dark, #3b7ad8);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* A field with a mark standing in it keeps the room for the mark: the
   tighter padding above would have put the text under the magnifier. */
.search-box input,
.search-input,
.modal-search-input-wrapper input {
    padding-left: 30px;
}

/* Outweighing the field's own padding, which is written as .search-box
   .search-input and so outranks a plain descendant rule. */
.search-box input.search-input,
.modal-search-input-wrapper input {
    padding-left: 32px;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-sm {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-sm:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}


/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── The column list, at the table's scale ─────────────────────────────
 *
 * Forty-one rows of 12px padding in bordered white cards on a grey field,
 * with a blue instruction panel over them and a filled blue pill for the
 * chosen side — the dialogue was as loud as the table it configures and
 * four screens long. Rows now, parted by a hairline, on white. */
.columns-instructions {
  padding: 8px 10px;
  background: #f4f9fd;
  border: 1px solid #dde3ea;
  border-left: 2px solid var(--primary);
  border-radius: 8px;
}

.columns-instructions p {
  font-size: 11px;
  line-height: 1.45;
  color: #64748b;
}

.columns-actions {
  gap: 6px;
}

/* Which side is being set: a quiet pair, the chosen one marked by its edge
   and its colour rather than by a filled pill. */
.type-filter-bar {
  gap: 8px;
}

.type-filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.type-filter-btn {
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  background: #fff;
  color: var(--text-primary);
}

.type-filter-btn:hover {
  background: #fff;
  border-color: var(--primary);
  color: var(--primary);
}

.type-filter-btn.active {
  background: #f4f9fd;
  border-color: var(--primary);
  color: var(--primary);
}

.columns-list {
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 4px;
  background: #fff;
}

.column-item {
  gap: 9px;
  padding: 5px 8px;
  background: transparent;
  border: none;
  border-radius: 6px;
  margin-bottom: 0;
  transition: background 0.15s ease;
}

.column-item + .column-item {
  border-top: 1px solid #f1f5f9;
}

.column-item:hover {
  background: #fbfcfd;
  border-color: transparent;
  box-shadow: none;
}

.column-item.disabled {
  opacity: 0.55;
}

.drag-handle {
  width: 16px;
  height: 16px;
  color: #cbd5e1;
}

.drag-handle svg {
  width: 14px;
  height: 14px;
}

/* Written with the element as well as the class: the field rules above are
   .modal-body input[type="number"], which outranks a class on its own. */
input.column-order,
.column-item input.column-order {
  width: 36px;
  height: 24px;
  padding: 0 4px;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: #64748b;
}

.column-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.column-key {
  font-size: 10px;
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* The switch, as everywhere: a rail with a handle. */
.column-toggle {
  position: relative;
  display: inline-block;
  width: 30px;
  height: 17px;
  flex-shrink: 0;
  cursor: pointer;
}

.column-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.column-toggle .toggle-track {
  position: absolute;
  inset: 0;
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.column-toggle .toggle-track::before {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 2.5px;
  top: 50%;
  margin-top: -4.5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.column-toggle input:checked + .toggle-track {
  /* The earlier rule in this file fills the rail blue; the rail is an
     outline now, so the fill is cancelled here as well as the edge set. */
  background: transparent;
  border-color: var(--primary);
}

.column-toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(13px);
}

.column-toggle:hover .toggle-track {
  border-color: var(--primary);
}

.selected-count {
  font-size: 11px;
  color: #64748b;
}

.columns-search .search-input {
  height: 32px;
  padding: 0 10px 0 10px;
  font-size: 13px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>
